// src/pages/Dashboard.js

import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { Line } from 'react-chartjs-2';
import '../styles/Dashboard.css';
import LeadsChart from '../components/LeadsChart';
import Notification from '../components/Notification';

function Dashboard() {
  const [data, setData] = useState(null);
  const [leadsStats, setLeadsStats] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  const [tokenReady, setTokenReady] = useState(false);
  const [token, setToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      setTokenReady(true);
    } else {
      const retry = setInterval(() => {
        const retryToken = localStorage.getItem('token');
        if (retryToken) {
          setToken(retryToken);
          setTokenReady(true);
          clearInterval(retry);
        }
      }, 300); // check every 300ms
      return () => clearInterval(retry);
    }
  }, []);

  useEffect(() => {
    if (!tokenReady || !token) return;

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [dashboardResponse, statsResponse, activityResponse] = await Promise.all([
          axiosInstance.get('/api/dashboard', { headers }),
          axiosInstance.get('/api/leads/stats?days=30', { headers }),
          axiosInstance.get('/api/recent-activity', { headers })
        ]);

        setData(dashboardResponse.data);
        setLeadsStats(statsResponse.data);
        setRecentActivity(Array.isArray(activityResponse.data) ? activityResponse.data : []);

        if (dashboardResponse.data.totalLeads > 0) {
          addNotification(`You have ${dashboardResponse.data.totalLeads} total leads!`, 'success');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tokenReady, token]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode);
      return newMode;
    });
  };

  const addNotification = (message, type) => {
    setNotifications((prev) => [...prev, { message, type }]);
  };

  const handleDateRangeChange = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axiosInstance.get(
        `/api/leads/stats?start=${customDateRange.start}&end=${customDateRange.end}`,
        { headers }
      );

      setLeadsStats(response.data);
    } catch (err) {
      console.error('Error fetching leads stats for custom date range:', err);
      setError('Failed to load data for the selected date range.');
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const chartData = {
    labels: leadsStats.map((stat) => stat.date),
    datasets: [
      {
        label: 'Leads Over Time',
        data: leadsStats.map((stat) => stat.count),
        borderColor: '#003366',
        backgroundColor: 'rgba(0, 51, 102, 0.5)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
        },
        grid: {
          display: true,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Leads',
        },
        grid: {
          display: true,
        },
      },
    },
  };

  return (
    <div className={`dashboard-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <header className="dashboard-header">
        <h1>Dashboard</h1>
        <button className="dark-mode-toggle" onClick={toggleDarkMode}>
          {isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </header>

      <div className="notifications-container">
        {notifications.map((note, index) => (
          <Notification
            key={index}
            message={note.message}
            type={note.type}
            onClose={() =>
              setNotifications((prev) => prev.filter((_, i) => i !== index))
            }
          />
        ))}
      </div>

      <div className="dashboard-cards">
        <div className="dashboard-card">
          <h2>Total Leads</h2>
          <p>{data.totalLeads}</p>
        </div>
        <div className="dashboard-card">
          <h2>Converted Leads</h2>
          <p>{data.convertedLeads}</p>
        </div>
        <div className="dashboard-card">
          <h2>Conversion Rate</h2>
          <p>{data.conversionRate}%</p>
        </div>
      </div>

      <div className="date-range-picker">
        <h2>Filter By Date Range</h2>
        <input
          type="date"
          value={customDateRange.start}
          onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
        />
        <input
          type="date"
          value={customDateRange.end}
          onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
        />
        <button onClick={handleDateRangeChange}>Apply</button>
      </div>

      <div className="dashboard-chart">
        <h2>Inline Chart</h2>
        {leadsStats.length > 0 ? (
          <div style={{ height: '400px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <p>No leads data available for the selected period.</p>
        )}
      </div>

      <div className="dashboard-chart">
        <h2>LeadsChart Component</h2>
        <LeadsChart />
      </div>

      <div className="dashboard-recent-activity">
        <h2>Recent Activity</h2>
        {recentActivity.length > 0 ? (
          <ul>
            {recentActivity.map((activity, index) => (
              <li key={index}>{activity}</li>
            ))}
          </ul>
        ) : (
          <p>No recent activity available.</p>
        )}
      </div>
    </div>
  );
}

export default Dashboard;